import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

function TeamAvailability() {
  const { teamId } = useParams();
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [availabilityList, setAvailabilityList] = useState([]);
  const [playersMap, setPlayersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [activeTab, setActiveTab] = useState("available");

  // Color scheme
  const colors = {
    primary: "#1a365d",
    accent: "#4299e1",
    green: "#38a169",
    greenLight: "#c6f6d5",
    red: "#e53e3e",
    redLight: "#fed7d7",
    yellow: "#d69e2e",
    yellowLight: "#fefcbf",
    background: "#f7fafc",
    cardBg: "#ffffff",
    text: "#2d3748",
    textMuted: "#718096",
    border: "#e2e8f0",
    borderLight: "#edf2f7",
  };

  // 1. Fetch all games for the team - using both old and new data models
  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        console.log("Fetching games for teamId:", teamId);
        
        let allGames = [];
        
        // Try new data model first
        try {
          const teamGamesQuery = query(
            collection(db, "games"),
            where("teamId", "==", teamId),
            orderBy("date")
          );
          
          const teamGamesSnap = await getDocs(teamGamesQuery);
          console.log("Team games found with new model:", teamGamesSnap.size);
          
          if (!teamGamesSnap.empty) {
            // Use new data model
            allGames = teamGamesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          }
        } catch (err) {
          console.error("Error fetching games with new model:", err);
        }
        
        // Fall back to old data model if needed
        if (allGames.length === 0) {
          console.log("Trying old data model...");
          
          try {
            const homeQuery = query(
              collection(db, "games"), 
              where("homeTeamId", "==", teamId),
              orderBy("date")
            );
            
            const homeSnap = await getDocs(homeQuery);
            console.log("Home games found:", homeSnap.size);
            
            const homeGames = homeSnap.docs.map((d) => ({ 
              id: d.id, 
              ...d.data(),
              isHomeTeam: true 
            }));
            
            allGames = [...homeGames];
          } catch (homeErr) {
            console.error("Error fetching home games:", homeErr);
          }
          
          try {
            const awayQuery = query(
              collection(db, "games"), 
              where("awayTeamId", "==", teamId),
              orderBy("date")
            );
            
            const awaySnap = await getDocs(awayQuery);
            console.log("Away games found:", awaySnap.size);
            
            const awayGames = awaySnap.docs.map((d) => ({ 
              id: d.id, 
              ...d.data(),
              isHomeTeam: false 
            }));
            
            allGames = [...allGames, ...awayGames];
          } catch (awayErr) {
            console.error("Error fetching away games:", awayErr);
          }
        }
        
        console.log("Total games found:", allGames.length);
        
        // Sort all games by date
        allGames.sort((a, b) => new Date(a.date) - new Date(b.date));
        setGames(allGames);
        
        // Set first upcoming game as default selection
        const today = new Date();
        const upcomingGames = allGames.filter(g => new Date(g.date) >= today);
        if (upcomingGames.length > 0) {
          setSelectedGameId(upcomingGames[0].id);
          setSelectedGame(upcomingGames[0]);
        } else if (allGames.length > 0) {
          setSelectedGameId(allGames[0].id);
          setSelectedGame(allGames[0]);
        }
        
        // Fetch total team players count
        console.log("Fetching player count from rosters");
        
        // From your Firestore screenshot, it looks like you have a teamId field in rosters collection
        const rostersQuery = query(
          collection(db, "rosters"),
          where("teamId", "==", teamId)
        );
        
        const rostersSnap = await getDocs(rostersQuery);
        console.log("Roster entries found:", rostersSnap.size);
        
        setTotalPlayers(rostersSnap.size);
      } catch (err) {
        console.error("Error fetching games for availability:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGames();
  }, [teamId]);

  // 2. When user selects a game, fetch availability
  useEffect(() => {
    if (!selectedGameId) return;
    
    const fetchAvailability = async () => {
      setLoading(true);
      try {
        console.log("Fetching availability for game:", selectedGameId);
        
        // Find selected game data
        const gameData = games.find(g => g.id === selectedGameId);
        setSelectedGame(gameData);
        
        // Get all availability docs for this game
        const qAvail = query(
          collection(db, "gameAvailability"),
          where("teamId", "==", teamId),
          where("gameId", "==", selectedGameId)
        );
        
        const snapAvail = await getDocs(qAvail);
        console.log("Availability entries found:", snapAvail.size);
        
        const items = snapAvail.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          timestamp: docSnap.data().timestamp?.toDate?.() || new Date(),
        }));
        
        setAvailabilityList(items);

        // Collect unique playerIds
        const playerIds = [...new Set(items.map((item) => item.playerId))];
        console.log("Unique player IDs:", playerIds.length);
        
        // Fetch user documents for these players
        const newPlayersMap = {};
        
        for (let pid of playerIds) {
          console.log("Fetching player data for:", pid);
          
          try {
            // Try to get player from users collection first (based on your summary)
            const userDoc = await getDoc(doc(db, "users", pid));
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log("Found user document:", userData.firstName);
              
              newPlayersMap[pid] = {
                name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
                photoURL: userData.photoURL || null,
                email: userData.email || null
              };
            } else {
              // Try the rosters collection as a backup
              console.log("User document not found, trying rosters...");
              
              // Looking for documents where playerId matches our pid
              const rosterQuery = query(
                collection(db, "rosters"),
                where("playerId", "==", pid),
                where("teamId", "==", teamId)
              );
              
              const rosterSnap = await getDocs(rosterQuery);
              
              if (!rosterSnap.empty) {
                const rosterData = rosterSnap.docs[0].data();
                console.log("Found in roster:", rosterData.playerName);
                
                newPlayersMap[pid] = {
                  name: rosterData.playerName || "Unnamed Player",
                  photoURL: null,
                  email: rosterData.playerEmail || null
                };
              } else {
                // Fall back to players collection
                console.log("Roster not found, trying players collection...");
                const playerDoc = await getDoc(doc(db, "players", pid));
                
                if (playerDoc.exists()) {
                  const playerData = playerDoc.data();
                  console.log("Found player document");
                  
                  newPlayersMap[pid] = {
                    name: `${playerData.firstName || ""} ${playerData.lastName || ""}`.trim(),
                    photoURL: playerData.avatarUrl || null,
                    email: playerData.email || null
                  };
                } else {
                  console.log("No data found for player ID:", pid);
                  newPlayersMap[pid] = { name: "Unknown Player" };
                }
              }
            }
          } catch (err) {
            console.error(`Error fetching player ${pid}:`, err);
            newPlayersMap[pid] = { name: "Unknown Player" };
          }
        }
        
        console.log("Final players map size:", Object.keys(newPlayersMap).length);
        setPlayersMap(newPlayersMap);
      } catch (err) {
        console.error("Error fetching availability:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailability();
  }, [selectedGameId, teamId, games]);

  // Separate into available, unavailable and tentative
  const availableList = availabilityList.filter((a) => a.availability === "available");
  const unavailableList = availabilityList.filter((a) => a.availability === "unavailable");
  const tentativeList = availabilityList.filter((a) => a.availability === "tentative" || a.availability === "maybe");
  
  // Calculate response rate
  const responseRate = totalPlayers > 0 ? 
    Math.round((availabilityList.length / totalPlayers) * 100) : 0;
  
  // Format date and time
  const formatGameDate = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Determine if a team is home/away - works with both data models
  const isHomeTeam = (game) => {
    if (game.teamId) {
      // New data model doesn't have home/away distinction
      return true;
    }
    // Old data model
    return game.homeTeamId === teamId;
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const renderPlayerItem = (item) => {
    const player = playersMap[item.playerId] || { name: "Unknown" };
    return (
      <div 
        key={item.id} 
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.borderLight}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div 
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: colors.borderLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {player.photoURL ? (
              <img 
                src={player.photoURL} 
                alt={player.name || "Player"} 
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : getInitials(player.name)}
          </div>
          <span>{player.name || "Unnamed Player"}</span>
        </div>
        {item.note && (
          <div 
            style={{
              fontSize: "12px",
              padding: "2px 8px",
              borderRadius: "9999px",
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.background,
            }}
          >
            Has note
          </div>
        )}
      </div>
    );
  };
  
  const tabStyle = (tabName) => ({
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    backgroundColor: activeTab === tabName ? 
      (tabName === "available" ? colors.greenLight : 
       tabName === "unavailable" ? colors.redLight : 
       colors.yellowLight) : 
      "transparent",
    color: activeTab === tabName ? 
      (tabName === "available" ? colors.green : 
       tabName === "unavailable" ? colors.red : 
       colors.yellow) : 
      colors.text,
    fontWeight: activeTab === tabName ? "bold" : "normal",
    border: `1px solid ${activeTab === tabName ? 
      (tabName === "available" ? colors.green : 
       tabName === "unavailable" ? colors.red : 
       colors.yellow) : 
      "transparent"}`,
  });

  if (loading && !games.length) {
    return (
      <div 
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "250px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div 
            style={{
              width: "32px",
              height: "32px",
              border: `2px solid ${colors.borderLight}`,
              borderTopColor: colors.primary,
              borderRadius: "50%",
              margin: "0 auto",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <p style={{ marginTop: "8px", color: colors.textMuted }}>Loading team data...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        maxWidth: "1200px", 
        margin: "2rem auto", 
        padding: "1rem",
      }}
    >
      <div
        style={{
          backgroundColor: colors.cardBg,
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: `1px solid ${colors.borderLight}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, color: colors.primary, fontSize: "20px", fontWeight: "bold" }}>
            Team Availability
          </h2>
          {selectedGame && (
            <div 
              style={{
                backgroundColor: colors.background,
                padding: "4px 12px",
                borderRadius: "9999px",
                fontSize: "14px",
                color: colors.textMuted,
              }}
            >
              {responseRate}% Response Rate
            </div>
          )}
        </div>
        
        {/* Card Content */}
        <div style={{ padding: "24px" }}>
          {/* Game Selector */}
          <div style={{ marginBottom: "24px" }}>
            <label 
              htmlFor="gameSelect" 
              style={{ 
                display: "block", 
                marginBottom: "8px",
                fontSize: "14px", 
                fontWeight: "500",
                color: colors.text, 
              }}
            >
              Select a Game:
            </label>
            <select
              id="gameSelect"
              value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value)}
              style={{
                width: "100%",
                maxWidth: "500px",
                padding: "8px 12px",
                borderRadius: "4px",
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: "16px",
              }}
            >
              <option value="">-- Choose a game --</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.opponentName} - {formatGameDate(g.date)} @ {g.time}
                </option>
              ))}
            </select>
          </div>

          {selectedGame && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {/* Game Details */}
              <div
                style={{
                  backgroundColor: colors.background,
                  padding: "16px",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  '@media (min-width: 768px)': {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                    {isHomeTeam(selectedGame) ? 'Home vs ' : 'Away @ '}
                    {selectedGame.opponentName}
                  </h3>
                  <div 
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      fontSize: "14px",
                      color: colors.textMuted,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>📅</span>
                      <span>{formatGameDate(selectedGame.date)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>🕒</span>
                      <span>{selectedGame.time}</span>
                    </div>
                    {selectedGame.locationName && (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span>📍</span>
                        <span>{selectedGame.locationName}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                  <div 
                    style={{
                      backgroundColor: colors.greenLight,
                      color: colors.green,
                      padding: "4px 12px",
                      borderRadius: "4px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>👥</span>
                    {availableList.length} Available
                  </div>
                  <div 
                    style={{
                      backgroundColor: colors.redLight,
                      color: colors.red,
                      padding: "4px 12px",
                      borderRadius: "4px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>👥</span>
                    {unavailableList.length} Unavailable
                  </div>
                  {tentativeList.length > 0 && (
                    <div 
                      style={{
                        backgroundColor: colors.yellowLight,
                        color: colors.yellow,
                        padding: "4px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>👥</span>
                      {tentativeList.length} Tentative
                    </div>
                  )}
                </div>
              </div>

              {/* Availability Tabs */}
              <div>
                <div 
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "8px",
                    marginBottom: "16px",
                  }}
                >
                  <div 
                    style={tabStyle("available")}
                    onClick={() => setActiveTab("available")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                      <div 
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: colors.green,
                        }}
                      ></div>
                      Available ({availableList.length})
                    </div>
                  </div>
                  <div 
                    style={tabStyle("unavailable")}
                    onClick={() => setActiveTab("unavailable")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                      <div 
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: colors.red,
                        }}
                      ></div>
                      Unavailable ({unavailableList.length})
                    </div>
                  </div>
                  <div 
                    style={tabStyle("tentative")}
                    onClick={() => setActiveTab("tentative")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                      <div 
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: colors.yellow,
                        }}
                      ></div>
                      Tentative ({tentativeList.length})
                    </div>
                  </div>
                </div>
                
                {/* Tab content */}
                <div style={{ marginTop: "16px" }}>
                  {activeTab === "available" && (
                    <>
                      {availableList.length === 0 ? (
                        <p 
                          style={{ 
                            textAlign: "center", 
                            padding: "32px 0", 
                            color: colors.textMuted 
                          }}
                        >
                          No players marked as available yet
                        </p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {availableList.map(renderPlayerItem)}
                        </div>
                      )}
                    </>
                  )}
                  
                  {activeTab === "unavailable" && (
                    <>
                      {unavailableList.length === 0 ? (
                        <p 
                          style={{ 
                            textAlign: "center", 
                            padding: "32px 0", 
                            color: colors.textMuted 
                          }}
                        >
                          No players marked as unavailable
                        </p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {unavailableList.map(renderPlayerItem)}
                        </div>
                      )}
                    </>
                  )}
                  
                  {activeTab === "tentative" && (
                    <>
                      {tentativeList.length === 0 ? (
                        <p 
                          style={{ 
                            textAlign: "center", 
                            padding: "32px 0", 
                            color: colors.textMuted 
                          }}
                        >
                          No players marked as tentative
                        </p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {tentativeList.map(renderPlayerItem)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          
          {!selectedGameId && (
            <div 
              style={{ 
                textAlign: "center", 
                padding: "48px 0", 
                color: colors.textMuted 
              }}
            >
              {games.length === 0 ? (
                <p>No upcoming games found for this team.</p>
              ) : (
                <p>Select a game to view player availability.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamAvailability;